"use client";
import type { GameData } from "@/app/games/(details)/types";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import {
  GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { cn } from "@/lib/utils";
import { PlayButton } from "@components/NewGamePage/TrailerPlayer/PlayButton";
import { Video as VideoIcon } from "lucide-react";
import { useCallback, useState } from "react";
import ReactPlayer from "react-player-v3";
import { VideoModal } from "./VideoModal";

export interface VideoCardProps {
  gameData: GameData;
  className?: string;
}

export function TrailerPlayer({ gameData, className }: VideoCardProps) {
  const { track } = useGamePageAnalytics();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenChange = useCallback(() => {
    setIsModalOpen((prev) => !prev);

    track({
      action: Action.PRESSED,
      extras: {
        control: "play-trailer",
        controlType: ControlType.BUTTON,
        section: GamePageSection.BENTO_BOXES,
      },
    });
  }, [track]);

  const handleMouseEnter = useCallback(() => {
    track({
      action: Action.HOVER,
      extras: {
        control: "play-trailer",
        controlType: ControlType.BUTTON,
        section: GamePageSection.BENTO_BOXES,
      },
    });
  }, [track]);

  return (
    <>
      <div
        // biome-ignore lint/a11y/useSemanticElements: use div instead of button as we have child elements not supported by <button>
        role="button"
        tabIndex={0}
        onClick={handleOpenChange}
        onMouseEnter={handleMouseEnter}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && handleOpenChange()
        }
        aria-label="Play video"
        className={cn(
          "relative aspect-video glass-border glass-border-hover group cursor-pointer rounded-[1.25rem]",
          className,
        )}
      >
        <div className="absolute inset-0 overflow-hidden rounded-[1.25rem] bg-background/50">
          <ReactPlayer
            src={gameData.gameContent?.trailer_url}
            playing={!isModalOpen}
            loop
            muted
            controls={false}
            playsInline
            width="100%"
            height="100%"
            className="size-full [--media-object-fit:cover]"
            fallback={<div className="w-full h-full bg-background/50" />}
          />
        </div>

        {/* Dim overlay */}
        <div className="absolute inset-0 size-full rounded-[1.25rem] pointer-events-none bg-darken-overlay" />

        {/* Title */}
        <div className="absolute left-[5%] top-[10%] text-neutral-50 font-suisse flex items-center gap-x-1.5 @4xl:flex-col @4xl:items-start">
          <VideoIcon className="@md:size-6 size-5" />
          <p className="@md:text-lg text-sm font-semibold">Official Trailer</p>
        </div>

        <PlayButton />
      </div>

      <VideoModal
        isModalOpen={isModalOpen}
        handleOpenChange={handleOpenChange}
        gameData={gameData}
      />
    </>
  );
}
